// Copyright 2025-2026 Florian Gallon. All Rights reserved.
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { auth } from '../firebase';
import { verifyPasswordResetCode, confirmPasswordReset } from 'firebase/auth';
import { useLanguage } from '../contexts/LanguageContext';
import Button from './Button';

export default function PasswordResetPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { language } = useLanguage();
  
  const [status, setStatus] = useState('loading'); // loading, form, success, error
  const [error, setError] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [actionCode, setActionCode] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const verifyCode = async () => {
      try {
        const code = searchParams.get('oobCode');
        const mode = searchParams.get('mode');
        
        if (!code || mode !== 'resetPassword') {
          setError('Invalid password reset link');
          setStatus('error');
          return;
        }

        setActionCode(code);
        
        // Verify the password reset code and get email
        const emailAddress = await verifyPasswordResetCode(auth, code);
        setEmail(emailAddress);
        setStatus('form');
        
      } catch (error) {
        console.error('Password reset verification error:', error);
        setError(language === 'fr' 
          ? 'Lien de récupération invalide ou expiré' 
          : 'Invalid or expired recovery link'
        );
        setStatus('error');
      }
    };

    verifyCode();
  }, [searchParams, language]);

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      setError(language === 'fr' 
        ? 'Les mots de passe ne correspondent pas' 
        : 'Passwords do not match'
      );
      return;
    }

    if (newPassword.length < 6) {
      setError(language === 'fr' 
        ? 'Le mot de passe doit contenir au moins 6 caractères' 
        : 'Password must be at least 6 characters'
      );
      return;
    }

    setLoading(true);
    setError('');

    try {
      await confirmPasswordReset(auth, actionCode, newPassword);
      setStatus('success');
    } catch (error) {
      console.error('Password reset error:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-[var(--bg-primary)] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="glass-effect rounded-3xl shadow-modern-lg p-8 lg:p-12 border border-white/30">
          
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-block p-6 bg-[var(--bg-secondary)] rounded-3xl shadow-xl mb-6">
              <img src="/Logo-digi-bakery-512.png" alt="Logo" className="w-24 h-24 mx-auto" />
            </div>
            <h1 className="text-3xl font-black text-[var(--text-primary)] mb-2">
              <span className="text-[var(--action-primary)]">Digi-Bakery</span>
            </h1>
            <p className="text-[var(--text-secondary)] text-lg font-medium">
              {language === 'fr' ? 'Réinitialisation du mot de passe' : 'Password Reset'}
            </p>
          </div>

          {/* Loading State */}
          {status === 'loading' && (
            <div className="text-center py-8">
              <div className="inline-block p-4 bg-[var(--bg-secondary)] rounded-2xl mb-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--action-primary)] mx-auto"></div>
              </div>
              <p className="text-[var(--text-secondary)]">{language === 'fr' ? 'Vérification du lien...' : 'Verifying link...'}</p>
            </div>
          )}

          {/* Error State */}
          {status === 'error' && (
            <div className="text-center py-8">
              <div className="inline-block p-4 bg-gradient-to-br from-red-100 to-pink-100 rounded-2xl mb-4">
                <div className="text-4xl">❌</div>
              </div>
              <h2 className="text-xl font-bold text-[var(--text-primary)] mb-2">
                {language === 'fr' ? 'Lien invalide' : 'Invalid Link'}
              </h2>
              <p className="text-[var(--text-secondary)] mb-6">{error}</p>
              <div className="space-y-3">
                <Button
                  onClick={() => navigate('/login')}
                  className="w-full py-3 rounded-2xl shadow-modern-lg"
                >
                  {language === 'fr' ? 'Retour à la connexion' : 'Back to Login'}
                </Button>
              </div>
            </div>
          )}

          {/* Password Reset Form */}
          {status === 'form' && (
            <div>
              <div className="bg-gradient-to-r from-[var(--bg-secondary)] to-[var(--bg-secondary)] border-2 border-[var(--border-subtle)] p-4 rounded-2xl mb-6">
                <div className="flex items-center gap-3">
                  <div className="text-2xl">🔒</div>
                  <div>
                    <div className="font-semibold text-[var(--text-primary)]">
                      {language === 'fr' ? 'Sécurisé' : 'Secure'}
                    </div>
                    <div className="text-sm text-[var(--text-secondary)]">
                      {language === 'fr' 
                        ? `Réinitialisation pour ${email}`
                        : `Resetting password for ${email}`
                      }
                    </div>
                  </div>
                </div>
              </div>

              {error && (
                <div className="bg-gradient-to-r from-red-50 to-pink-50 border-2 border-red-200 text-red-800 p-4 rounded-2xl mb-6">
                  {error}
                </div>
              )}

              <form onSubmit={handlePasswordReset} className="space-y-6">
                <div>
                  <label className="block text-sm font-bold text-[var(--text-primary)] mb-2">
                    {language === 'fr' ? 'Nouveau mot de passe' : 'New Password'}
                  </label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full border-2 border-[var(--border-subtle)] rounded-2xl px-4 py-3 focus:outline-none focus:ring-4 focus:ring-[var(--focus-ring)] focus:border-[var(--action-primary)] transition-all duration-300 bg-[var(--surface-card)]/80 backdrop-blur-sm"
                    placeholder="••••••••"
                    required
                    minLength={6}
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-[var(--text-primary)] mb-2">
                    {language === 'fr' ? 'Confirmer le mot de passe' : 'Confirm Password'}
                  </label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full border-2 border-[var(--border-subtle)] rounded-2xl px-4 py-3 focus:outline-none focus:ring-4 focus:ring-[var(--focus-ring)] focus:border-[var(--action-primary)] transition-all duration-300 bg-[var(--surface-card)]/80 backdrop-blur-sm"
                    placeholder="••••••••"
                    required
                    minLength={6}
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full py-4 rounded-2xl shadow-modern-lg hover:shadow-lifted"
                >
                  {loading 
                    ? (language === 'fr' ? 'Réinitialisation...' : 'Resetting...') 
                    : (language === 'fr' ? 'Réinitialiser le mot de passe' : 'Reset Password')
                  }
                </Button>
              </form>
            </div>
          )}

          {/* Success State */}
          {status === 'success' && (
            <div className="text-center py-8">
              <div className="inline-block p-6 bg-gradient-to-br from-green-100 to-emerald-100 rounded-3xl shadow-modern-lg mb-6">
                <div className="text-6xl">✅</div>
              </div>
              <h2 className="text-2xl font-bold text-[var(--text-primary)] mb-4">
                {language === 'fr' ? 'Mot de passe réinitialisé!' : 'Password Reset Successful!'}
              </h2>
              <p className="text-[var(--text-secondary)] mb-6 leading-relaxed">
                {language === 'fr' 
                  ? 'Votre mot de passe a été mis à jour avec succès. Vous pouvez maintenant vous connecter avec votre nouveau mot de passe.'
                  : 'Your password has been successfully updated. You can now sign in with your new password.'
                }
              </p>
              <div className="space-y-3">
                <Button
                  onClick={handleBackToLogin}
                  className="w-full py-3 rounded-2xl shadow-modern-lg hover:shadow-lifted"
                >
                  {language === 'fr' ? 'Se connecter' : 'Sign In'}
                </Button>
                <Button
                  onClick={() => navigate('/')}
                  variant="ghost"
                  className="w-full text-sm py-2 rounded-xl text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                >
                  {language === 'fr' ? 'Retour à l\'accueil' : 'Back to Home'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
